import { minionEarnConfig } from "constants/minion.constants";

const {
  DAILY_POINT_CAP,
  SPARKEV_MIN_AMOUNT,
  SPECIAL_POINT,
  MALL_SHOP_MIN_AMOUNT,
  MALL_SHOP_POINT,
} = minionEarnConfig;

const RECEIPT_DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const RECEIPT_DATE_PREFIX_RE = /^(\d{4})-(\d{2})-(\d{2})/;
const RECEIPT_ISO_PREFIX_RE = /^(\d{4}-\d{2}-\d{2})T/;
const THAI_OFFSET_MS = 7 * 60 * 60 * 1000;

function partsFromYmd(match) {
  return {
    year: parseInt(match[1], 10),
    month: parseInt(match[2], 10),
    day: parseInt(match[3], 10),
  };
}

function toThaiCalendarParts(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;

  const thai = new Date(d.getTime() + THAI_OFFSET_MS);
  return {
    year: thai.getUTCFullYear(),
    month: thai.getUTCMonth() + 1,
    day: thai.getUTCDate(),
  };
}

function parseReceiptDateParts(value) {
  if (value == null || value === "") return null;

  if (value instanceof Date) {
    return toThaiCalendarParts(value);
  }

  if (typeof value === "object" && typeof value.toDate === "function") {
    return toThaiCalendarParts(value.toDate());
  }

  const source = String(value).trim();

  if (RECEIPT_DATE_ONLY_RE.test(source)) {
    return partsFromYmd(source.match(RECEIPT_DATE_ONLY_RE));
  }

  if (RECEIPT_ISO_PREFIX_RE.test(source)) {
    // 2026-06-02T00:00:00.000+07:00 or without timezone — use YYYY-MM-DD in string
    if (!source.endsWith("Z")) {
      return partsFromYmd(source.match(RECEIPT_DATE_PREFIX_RE));
    }

    // 2026-06-06T17:00:00.000Z — UTC instant → Thailand calendar date
    return toThaiCalendarParts(source);
  }

  const prefix = source.match(RECEIPT_DATE_PREFIX_RE);
  if (prefix) {
    return partsFromYmd(prefix);
  }

  return toThaiCalendarParts(source);
}

function toDateKey(date) {
  const parts = parseReceiptDateParts(date);
  if (!parts) return null;

  const month = String(parts.month).padStart(2, "0");
  const day = String(parts.day).padStart(2, "0");
  return `${parts.year}-${month}-${day}`;
}

const RECEIPT_MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

export function formatMinionReceiptDate(value) {
  const parts = parseReceiptDateParts(value);
  if (!parts) return "";

  const day = String(parts.day).padStart(2, "0");
  const mon = RECEIPT_MONTH_NAMES[parts.month - 1];
  const year = String(parts.year).slice(-2);
  return `${day}-${mon}-${year}`;
}

export function formatMinionReceiptDateForSubmit(value) {
  return toDateKey(value) || "";
}

export function normalizeMinionReceiptDate(value) {
  return formatMinionReceiptDateForSubmit(value);
}

function firstNonEmpty(...values) {
  for (const value of values) {
    if (value != null && value !== "") return value;
  }
  return "";
}

export function getRowBranch(row) {
  if (!row) return "";

  return firstNonEmpty(
    row.branch,
    row.store_name,
    row.storeName,
    row.branchName,
    row.lotusBranch,
    row.receipt?.branch,
    row.detail?.branch,
    row.transaction?.branch
  );
}

export function getRowReceiptNumber(row) {
  if (!row) return "";

  return firstNonEmpty(
    row.receiptNumber,
    row.receipt_number,
    row.receiptNo,
    row.invoiceNumber,
    row.receipt?.receiptNumber,
    row.detail?.receiptNumber,
    row.transaction?.receiptNumber
  );
}

export function getRowProvince(row) {
  if (!row) return "";

  return firstNonEmpty(
    row.province,
    row.provinceName,
    row.receipt?.province,
    row.detail?.province,
    row.transaction?.province
  );
}

export function isMinionCurrentReceiptRow(
  item,
  { transactionId, transactionCode } = {}
) {
  const row = normalizeMinionReceiptRow(item);
  if (!row) return false;

  if (
    transactionId &&
    (row._id == transactionId || row.transactionId == transactionId)
  ) {
    return true;
  }

  if (transactionCode && row.code == transactionCode) {
    return true;
  }

  return false;
}

function normalizeMinionReceiptRow(row) {
  if (!row) return row;

  const id = row._id || row.transactionId;

  return {
    ...row,
    _id: id,
    transactionId: row.transactionId || row._id,
    receiptDate: row.receiptDate || row.receipt_date || row.created_at,
    receiptNumber: getRowReceiptNumber(row),
    priceSum: row.priceSum ?? row.price,
    branch: getRowBranch(row),
    province: getRowProvince(row),
  };
}

function mergeMinionReceiptRows(primary, secondary) {
  const left = normalizeMinionReceiptRow(primary);
  const right = normalizeMinionReceiptRow(secondary);

  return {
    ...left,
    ...right,
    branch: firstNonEmpty(left.branch, right.branch),
    province: firstNonEmpty(left.province, right.province),
    shop: firstNonEmpty(left.shop, right.shop),
    receiptNumber: firstNonEmpty(left.receiptNumber, right.receiptNumber),
    receiptDate: firstNonEmpty(left.receiptDate, right.receiptDate),
    priceSum: left.priceSum ?? right.priceSum,
    point: left.point ?? right.point,
    remark: firstNonEmpty(right.remark, left.remark),
  };
}

function putMergedReceipt(merged, receipt) {
  const normalized = normalizeMinionReceiptRow(receipt);
  const key = getReceiptKey(normalized);
  if (!key) return;

  const existing = merged.get(String(key));
  merged.set(
    String(key),
    existing ? mergeMinionReceiptRows(existing, normalized) : normalized
  );
}

function getReceiptKey(receipt) {
  if (!receipt) return null;
  return (
    receipt._id ||
    receipt.transactionId ||
    receipt.code ||
    receipt.uniqueId ||
    null
  );
}

function buildFieldLookup(sources = []) {
  const lookup = new Map();

  sources.forEach((source) => {
    if (!source) return;

    const normalized = normalizeMinionReceiptRow(source);
    const fields = {
      branch: normalized.branch,
      province: normalized.province,
      receiptNumber: normalized.receiptNumber,
    };

    [normalized._id, normalized.transactionId, normalized.code, normalized.uniqueId, normalized.receiptNumber]
      .filter(Boolean)
      .forEach((key) => {
        const existing = lookup.get(String(key)) || {};
        lookup.set(String(key), {
          branch: firstNonEmpty(existing.branch, fields.branch),
          province: firstNonEmpty(existing.province, fields.province),
          receiptNumber: firstNonEmpty(existing.receiptNumber, fields.receiptNumber),
        });
      });
  });

  return lookup;
}

function applyFieldLookup(receipt, lookup) {
  const normalized = normalizeMinionReceiptRow(receipt);
  let branch = normalized.branch;
  let province = normalized.province;
  let receiptNumber = normalized.receiptNumber;

  [normalized._id, normalized.transactionId, normalized.code, normalized.uniqueId, normalized.receiptNumber]
    .filter(Boolean)
    .forEach((key) => {
      const fields = lookup.get(String(key));
      if (!fields) return;

      branch = firstNonEmpty(branch, fields.branch);
      province = firstNonEmpty(province, fields.province);
      receiptNumber = firstNonEmpty(receiptNumber, fields.receiptNumber);
    });

  return {
    ...normalized,
    branch,
    province,
    receiptNumber,
  };
}

function dedupeReceiptsByCode(receipts = []) {
  const byCode = new Map();
  const withoutCode = [];

  receipts.forEach((receipt) => {
    const normalized = normalizeMinionReceiptRow(receipt);
    if (!normalized.code) {
      withoutCode.push(normalized);
      return;
    }

    const code = String(normalized.code);
    const existing = byCode.get(code);
    byCode.set(
      code,
      existing ? mergeMinionReceiptRows(existing, normalized) : normalized
    );
  });

  return [...byCode.values(), ...withoutCode];
}

function isSameReceiptDate(rowDateKey, dateKey) {
  if (!dateKey) return true;
  if (!rowDateKey) return true;
  return rowDateKey === dateKey;
}

function isApprovedReceipt(receipt) {
  return String(receipt?.remark || "").toUpperCase() === "SUCCESS";
}

function isSameReceipt(receipt, { transactionId, transactionCode } = {}) {
  if (
    transactionId &&
    (receipt._id === transactionId || receipt.transactionId === transactionId)
  ) {
    return true;
  }
  if (transactionCode && receipt.code === transactionCode) return true;
  return false;
}

export function buildMinionReceiptList(
  receiptList = [],
  { receiptDate, currentTransaction } = {}
) {
  const merged = new Map();
  const dateKey = toDateKey(receiptDate);
  const sources = Array.isArray(receiptList) ? receiptList : [];

  sources.forEach((receipt) => {
    putMergedReceipt(merged, receipt);
  });

  if (currentTransaction) {
    putMergedReceipt(merged, currentTransaction);
  }

  const allSources = [...sources, currentTransaction].filter(Boolean);
  const fieldLookup = buildFieldLookup(allSources);

  return dedupeReceiptsByCode(
    Array.from(merged.values())
      .map((receipt) => applyFieldLookup(receipt, fieldLookup))
      .filter((receipt) =>
        isSameReceiptDate(toDateKey(receipt.receiptDate), dateKey)
      )
  );
}

export function getMinionReceiptsForDate(receiptList = [], receiptDate) {
  const dateKey = toDateKey(receiptDate);
  if (!dateKey) return receiptList;
  return receiptList.filter((r) =>
    isSameReceiptDate(toDateKey(r.receiptDate), dateKey)
  );
}

export function calculateMinionEarnPoint(shop, priceSum, point) {
  const amount = Number(priceSum) || 0;
  if (shop === "Spark EV" && amount >= SPARKEV_MIN_AMOUNT) {
    return SPECIAL_POINT;
  }
  if (amount >= MALL_SHOP_MIN_AMOUNT) {
    return MALL_SHOP_POINT;
  }
  return Number(point) || 0;
}

function getApprovedPoint(receipt) {
  return Number(receipt.point) || 0;
}

function getApprovedReceipts(
  receiptList = [],
  { transactionId, transactionCode, receiptDate } = {}
) {
  const dateKey = toDateKey(receiptDate);

  return receiptList.filter((r) => {
    if (isSameReceipt(r, { transactionId, transactionCode })) return false;
    if (!isApprovedReceipt(r)) return false;
    const rowDateKey = toDateKey(r.receiptDate);
    if (dateKey && rowDateKey && rowDateKey !== dateKey) return false;
    if (dateKey && !rowDateKey) return false;
    return true;
  });
}

export function calculateMinionAdjustedPoint(
  receiptList = [],
  { transactionId, transactionCode, receiptDate, shop, priceSum, point } = {}
) {
  const earnedPoint = calculateMinionEarnPoint(shop, priceSum, point);
  const approvedPoints = getApprovedReceipts(receiptList, {
    transactionId,
    transactionCode,
    receiptDate,
  }).reduce((sum, r) => sum + getApprovedPoint(r), 0);
  const remaining = Math.max(0, DAILY_POINT_CAP - approvedPoints);
  const adjustedPoint = Math.min(earnedPoint, remaining);
  const discount = earnedPoint - adjustedPoint;

  return {
    earnedPoint,
    adjustedPoint,
    discount,
    approvedPoints,
    remaining,
    isReduced: discount > 0,
  };
}

export function calculateMinionSummaryWarnings(
  receiptList = [],
  {
    pendingPoint = 0,
    isAboutToApprove = false,
    transactionId,
    transactionCode,
    receiptDate,
  } = {}
) {
  if (!isAboutToApprove) {
    return [];
  }

  const approvedPoints = getApprovedReceipts(receiptList, {
    transactionId,
    transactionCode,
    receiptDate,
  }).reduce((sum, r) => sum + getApprovedPoint(r), 0);
  const pointToApprove = Number(pendingPoint) || 0;
  const totalAfterApprove = approvedPoints + pointToApprove;

  if (totalAfterApprove > DAILY_POINT_CAP) {
    const remaining = Math.max(0, DAILY_POINT_CAP - approvedPoints);
    const adjustedPoint = Math.min(pointToApprove, remaining);

    if (adjustedPoint < pointToApprove) {
      return [
        `จำนวนคะแนนครบ Quota ${DAILY_POINT_CAP} คะแนน/วัน (ลดคะแนนอัตโนมัติเหลือ ${adjustedPoint} คะแนน)`,
      ];
    }

    return [`จำนวนคะแนนครบ Quota ${DAILY_POINT_CAP} คะแนน/วัน`];
  }

  return [];
}

export function formatMinionTablePoint(item, receiptList) {
  const remark = String(item?.remark || "").toUpperCase();
  if (remark === "REJECT") {
    return 0;
  }

  if (isApprovedReceipt(item)) {
    return getApprovedPoint(item);
  }

  const adjustment = calculateMinionAdjustedPoint(receiptList, {
    transactionId: item._id,
    transactionCode: item.code,
    receiptDate: item.receiptDate,
    shop: item.shop,
    priceSum: item.priceSum,
    point: item.point,
  });

  if (adjustment.isReduced) {
    return `${adjustment.adjustedPoint} (จาก ${adjustment.earnedPoint})`;
  }

  return adjustment.adjustedPoint;
}
